import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert } from "@material-tailwind/react";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Input from "shared/Input/Input";
import OnboardingTour, { stepStyle } from "utils/tours/OnboardingTour";
import { z } from "zod";

const formSchema = z
  .object({
    email: z.string().email({ message: "Veuillez entrer une adresse email valide." }),
    emailConfirm: z.string().email(),
    password: z
      .string()
      .min(6, { message: "Le mot de passe doit contenir au moins 6 caractères." })
      .max(50, { message: "Le mot de passe ne peut pas dépasser 50 caractères." }),
    passwordConfirm: z.string(),
    role: z.string().min(1, { message: "Veuillez choisir une option." }),
    isOwner: z.boolean().optional(),
  })
  .refine((data) => data.email === data.emailConfirm, {
    message: "L'dresses e-mail ne correspondent pas.",
    path: ["emailConfirm"],
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Le mot de passe semble erroné. Veuillez réessayer.",
    path: ["passwordConfirm"],
  })
  .refine(
    (data) => {
      if (data.role === "client") {
        return data.isOwner !== undefined;
      }
      return true;
    },
    {
      message: "Veuillez indiquer si vous voulez devenir hôte.",
      path: ["isOwner"],
    }
  );

export interface UserFormProps {
  className?: string;
  isLoading: boolean;
  role: string;
  handleForm: Event;
  errorUserForm?: string;
  FormData: (data: any) => void;
}

const UserForm: FC<UserFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  role,
  handleForm,
  errorUserForm,
  FormData,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassowrd, setShowConfirmPassword] = useState(false);

  const steps = [
    {
      selector: ".email",
      content:
        "Une adresse électronique, également appelée adresse e-mail, est une chaîne de caractères (exemple@gmail.com) utilisé une adresse correcte.",
      stepStyle,
    },
    {
      selector: ".confirm-email",
      content: "Pour vérifier que vous avez saisi la bonne adresse e-mail.",
      stepStyle,
    },
    {
      selector: ".password ",
      content: "Un mot de passe utilisé pour accéder à votre compte.",
    },
    {
      selector: ".confirm-password ",
      content: "Pour vous assurer que vous avez saisi correctement le mot de passe.(Exemple-123).",
    },
  ];

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      role: role,
      isOwner: false,
    },
    mode: "onSubmit",
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    const { emailConfirm, passwordConfirm, ...formData } = data;
    FormData(formData);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassowrd);
  };

  useEffect(() => {
    const password = getValues("password") || "";
    const updatedRequirements = {
      minLength: password.length >= 6, // Changé de 8 à 6
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?À-ÿ]/.test(password),
    };

    setPasswordRequirements(updatedRequirements);
  }, [watch("password")]);

  useEffect(() => {
    if (handleForm.type === "chekFormEvent") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  return (
    <>
      <div className={className}>
        {errorUserForm && (
          <Alert color="red" className="text-xs">
            {errorUserForm}
          </Alert>
        )}

        <form className="grid grid-cols-1 gap-6">
          <label className="block email">
            <span className="text-neutral-800 dark:text-neutral-200">
              Adresse Email <span className="text-xs">*</span>
            </span>
            <Input
              type="email"
              placeholder="Saisir votre email"
              className={`mt-1 ${errors.email?.message ? "border-red-500" : ""}`}
              disabled={isLoading}
              {...register("email")}
              onCopy={(e) => e.preventDefault()}
            />
            {errors.email?.message && <div className="mt-1 text-xs text-red-500">{errors.email?.message}</div>}
          </label>

          <label className="block confirm-email">
            <span className="text-neutral-800 dark:text-neutral-200">
              Confirmation Adresse Email <span className="text-xs">*</span>
            </span>
            <Input
              type="email"
              placeholder="Saisir à nouveau votre email"
              className={`mt-1 ${errors.emailConfirm?.message ? "border-red-500" : ""}`}
              disabled={isLoading}
              {...register("emailConfirm")}
            />
            {errors.emailConfirm?.message && (
              <div className="mt-1 text-xs text-red-500">{errors.emailConfirm?.message}</div>
            )}
          </label>

          <label className="relative block password ">
            <span className="text-neutral-800 dark:text-neutral-200">
              Mot de passe <span className="text-xs ">*</span>
            </span>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Saisir votre mot de passe"
                className={`w-full pr-3 mt-1 ${errors.password?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                {...register("password")}
                onCopy={(e) => e.preventDefault()}
              />
              <span
                className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="flex items-center">
              {passwordRequirements.minLength ? (
                <CheckIcon className="w-4 h-4 mt-1 mr-1 text-green-700" />
              ) : (
                <XMarkIcon className="w-4 h-4 mt-1 mr-1" />
              )}
              <span className={`mt-1 text-sm ${passwordRequirements.minLength ?? "text-green-700"}`}>
                6 caractères minimum
              </span>
            </div>

            <div className="flex items-center">
              {passwordRequirements.hasLowercase ? (
                <CheckIcon className="w-4 h-4 mt-1 mr-1 text-green-700" />
              ) : (
                <XMarkIcon className="w-4 h-4 mt-1 mr-1" />
              )}
              <span className={`mt-1 text-sm ${passwordRequirements.hasLowercase ?? "text-green-700"}`}>
                1 minuscule
              </span>
            </div>

            <div className="flex items-center">
              {passwordRequirements.hasUppercase ? (
                <CheckIcon className="w-4 h-4 mt-1 mr-1 text-green-700" />
              ) : (
                <XMarkIcon className="w-4 h-4 mt-1 mr-1" />
              )}
              <span className={`mt-1 text-sm ${passwordRequirements.hasUppercase ?? "text-green-700"}`}>
                1 majuscule
              </span>
            </div>

            <div className="flex items-center">
              {passwordRequirements.hasNumber ? (
                <CheckIcon className="w-4 h-4 mt-1 mr-1 text-green-700" />
              ) : (
                <XMarkIcon className="w-4 h-4 mt-1 mr-1" />
              )}
              <span className={`mt-1 text-sm ${passwordRequirements.hasNumber ?? "text-green-700"}`}>
                1 chiffre
              </span>
            </div>

            <div className="flex items-center">
              {passwordRequirements.hasSpecialChar ? (
                <CheckIcon className="w-4 h-4 mt-1 mr-1 text-green-700" />
              ) : (
                <XMarkIcon className="w-4 h-4 mt-1 mr-1" />
              )}
              <span className={`mt-1 text-sm ${passwordRequirements.hasSpecialChar ?? "text-green-700"}`}>
                1 caractère spécial
              </span>
            </div>

            {errors.password?.message && (
              <div className="mt-1 text-xs text-red-500">{errors.password?.message}</div>
            )}
          </label>

          <label className="relative block confirm-password">
            <span className="text-neutral-800 dark:text-neutral-200">
              Confirmation Mot de passe <span className="text-xs ">*</span>
            </span>
            <div className="relative">
              <Input
                type={showConfirmPassowrd ? "text" : "password"}
                placeholder="Saisir à nouveau votre mot de passe"
                className={`w-full pr-3 mt-1 ${errors.password?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                {...register("passwordConfirm")}
                onCopy={(e) => e.preventDefault()}
              />
              <span
                className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassowrd ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {errors.passwordConfirm?.message && (
              <div className="mt-1 text-xs text-red-500">{errors.passwordConfirm?.message}</div>
            )}
          </label>

          {role === "client" && (
            <label className="block">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={isLoading}
                  {...register("isOwner")}
                />
                <span className="ml-2 text-neutral-800 dark:text-neutral-200">
                  Je suis propriétaire d'un espace
                </span>
              </div>
              {errors.isOwner?.message && (
                <div className="mt-1 text-xs text-red-500">{errors.isOwner?.message}</div>
              )}
            </label>
          )}
        </form>
      </div>
      <OnboardingTour steps={steps} page_name={"userForm"}></OnboardingTour>
    </>
  );
};

export default UserForm;
